
import GuardTracking from '../components/GuardTracking'
import { useState, useEffect } from 'react'
import { useDataStore } from '../stores/dataStore'
import { useAuthStore } from '../stores/authStore'
import { 
  FileText, 
  Box, 
  ClipboardList,
  Plus,
  Search,
  Download,
  Eye,
  Edit,
  Shield, Package,
  X,
  Trash2,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import AttachmentViewer from '../components/common/AttachmentViewer'
import './Records.css'
import DocumentList from '../components/DocumentList';

export default function Records() {
  const [activeTab, setActiveTab] = useState('assets')
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [searchTerm, setSearchTerm] = useState('')
  const [viewItem, setViewItem] = useState(null)
  const [auditFilter, setAuditFilter] = useState('')
  const [auditDateFrom, setAuditDateFrom] = useState('')
  const [auditDateTo, setAuditDateTo] = useState('')

    const { assets, fetchAssets, uniforms, fetchUniforms, audits, fetchAudits, policies, fetchPolicies } = useDataStore()
  const { user } = useAuthStore()
const isAdmin = user?.role === 'admin'
  const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'

   useEffect(() => {
    fetchAssets()
    fetchUniforms()
    fetchAudits()
    fetchPolicies()
  }, [])

  const showMessage = (type, text) => { 
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: '', text: '' }), 5000) 
  }

  const openModal = (type, item = null) => {
    setModalType(type)
    setSelectedItem(item)
    setFormData(item || getInitialFormData(type))
    setShowModal(true)
    setMessage({ type: '', text: '' })
  }

  const closeModal = () => { 
    setShowModal(false)
    setModalType('')
    setSelectedItem(null)
    setFormData({})
    setLoading(false) 
  }

  const openView = (item) => {
    setViewItem(item)
  }

  const closeView = () => {
    setViewItem(null)
  }

  const getInitialFormData = (type) => {
    switch(type) {
      case 'asset': return { name: '', category: 'equipment', serialNumber: '', status: 'available', condition: 'good', assignedTo: '' }
      case 'uniform': return { guardName: '', uniformType: 'Security Uniform', size: 'M', issueDate: '', condition: 'good' }
      case 'audit': return { title: '', date: '', description: '', auditor: 'Internal', status: 'completed' }
      case 'policy': return { title: '', description: '', type: 'policy' }
      default: return {}
    }
  }
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) {
      setFormData(prev => {
        const existingDocs = prev.documents || []
        const existingNames = prev.documentNames || []
        return { 
          ...prev, 
          documents: [...existingDocs, ...files],
          documentNames: [...existingNames, ...files.map(f => f.name)]
        }
      })
    }
  }
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

    const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const typeMap = { 'asset': 'assets', 'uniform': 'uniforms', 'audit': 'audits', 'policy': 'policies' }
      const apiType = typeMap[modalType] || 'assets'
      let endpoint = selectedItem ? `/${apiType}/${selectedItem._id || selectedItem.id}` : `/${apiType}`
      let method = selectedItem ? 'PUT' : 'POST'
      
      let body, headers
      if ((modalType === 'audit' || modalType === 'policy') && formData.documents) {
        const formDataObj = new FormData()
        Object.keys(formData).forEach(key => {
          if (key !== 'documents' && key !== 'documentNames' && formData[key] !== undefined && formData[key] !== null) {
            formDataObj.append(key, formData[key])
          }
        })
        if (formData.documents && formData.documents.length > 0) {
          formData.documents.forEach(file => {
            formDataObj.append('documents', file)
          })
        }
        body = formDataObj
        headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      } else {
        headers = { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        }
        body = JSON.stringify(formData)
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers,
        body
      })
      
      if (response.ok) {
        showMessage('success', `${modalType} saved successfully!`)
        if (modalType === 'asset') fetchAssets()
        else if (modalType === 'uniform') fetchUniforms()
        else if (modalType === 'audit') fetchAudits()
        else if (modalType === 'policy') fetchPolicies()
        setTimeout(() => closeModal(), 1500)
      } else {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }))
        showMessage('error', error.message || 'Operation failed')
      }
    } catch (err) { 
      showMessage('error', 'Network error. Please try again.') 
    } finally { 
      setLoading(false) 
    }
  }

  const handleDelete = async (item, type) => {
    if (!window.confirm(`Delete this ${type}?`)) return
    
    setLoading(true)
    try {
      const typeMap = { 'asset': 'assets', 'uniform': 'uniforms', 'audit': 'audits', 'policy': 'policies' }
      const apiType = typeMap[type] || 'assets'
      const response = await fetch(`${API_URL}/${apiType}/${item._id || item.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      
      if (response.ok) {
        showMessage('success', `${type} deleted!`)
        if (type === 'asset') fetchAssets()
        else if (type === 'uniform') fetchUniforms()
        else if (type === 'audit') fetchAudits()
        else if (type === 'policy') fetchPolicies()
      } else {
        showMessage('error', 'Delete failed')
      }
    } catch (err) { 
      showMessage('error', 'Network error.') 
    } finally { 
      setLoading(false) 
    }
  }

  const handleExport = () => {
    if (assets.length === 0) {
      showMessage('error', 'No data to export')
      return
    }
     
    const headers = ['Asset ID', 'Name', 'Category', 'Serial Number', 'Status', 'Condition', 'Assigned To']
    const csvRows = assets.map(asset => [
      asset.assetId,
      asset.name,
      asset.category,
      asset.serialNumber || 'N/A',
      asset.status,
      asset.condition,
      asset.assignedTo?.firstName || 'Unassigned'
    ].map(v => `"${v || ''}"`).join(','))
    
    const csv = [headers.join(','), ...csvRows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `assets_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    showMessage('success', 'Assets exported successfully!')
  }
    const handleExportAudits = () => {
    let filtered = audits
    if (auditFilter) {
      const term = auditFilter.toLowerCase()
      filtered = filtered.filter(a => 
        (a.title || '').toLowerCase().includes(term) ||
        (a.description || '').toLowerCase().includes(term) ||
        (a.auditor || '').toLowerCase().includes(term)
      )
    }
    if (auditDateFrom) {
      filtered = filtered.filter(a => {
        const d = a.date ? new Date(a.date) : null
        return d && !isNaN(d.getTime()) && d >= new Date(auditDateFrom)
      })
    }
    if (auditDateTo) {
      filtered = filtered.filter(a => {
        const d = a.date ? new Date(a.date) : null
        return d && !isNaN(d.getTime()) && d <= new Date(auditDateTo)
      })
    }
    
    if (filtered.length === 0) {
      showMessage('error', 'No audits to export')
      return
    }
    
    const headers = ['Title', 'Date', 'Auditor', 'Status', 'Risk Level', 'Description', 'Findings', 'Recommendations', 'Created At']
    const rows = filtered.map(a => [
      a.title || '',
      a.date ? new Date(a.date).toLocaleDateString('en-KE') : '',
      a.auditor || '',
      a.status || '',
      a.riskLevel || a.risk_level || '',
      (a.description || '').replace(/,/g, ';'),
      (a.findings || '').replace(/,/g, ';'),
      (a.recommendations || '').replace(/,/g, ';'),
      a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-KE') : ''
    ])
    
    let csv = headers.join(',') + '\n'
    rows.forEach(row => {
      csv += row.map(cell => `"${cell}"`).join(',') + '\n'
    })
    
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit_reports_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
    showMessage('success', `${filtered.length} audits exported!`)
  }  

  const filteredAssets = assets?.filter(a => {
    const search = searchTerm.toLowerCase()
    return a?.name?.toLowerCase().includes(search) || 
           a?.assetId?.toLowerCase().includes(search) ||
           a?.category?.toLowerCase().includes(search) ||
           a?.serialNumber?.toLowerCase().includes(search)
  }) || []

  const renderModal = () => {
    if (!showModal) return null
    
    const modalTitles = {
      asset: selectedItem ? 'Edit Asset' : 'Add New Asset',
      uniform: selectedItem ? 'Edit Uniform' : 'Add New Uniform',
      audit: selectedItem ? 'Edit Audit' : 'Add New Audit',
      policy: selectedItem ? 'Edit Policy' : 'Add New Policy'
    }

    return (
      <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
        <div className="modal-content">
          <div className="modal-header">
            <h3>{modalTitles[modalType] || 'Form'}</h3>
            <button onClick={closeModal}><X size={20} /></button>
          </div>
          
          {message.text && (
            <div className={`alert alert-${message.type}`}>
              {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {message.text}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {modalType === 'asset' && (
              <div className="form-grid">
                <div className="form-group form-col-2">
                  <label>Asset Name *</label>
                  <input type="text" name="name" value={formData.name || ''} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select name="category" value={formData.category || 'equipment'} onChange={handleInputChange}>
                    <option value="equipment">Equipment</option>
                    <option value="vehicle">Vehicle</option>
                    <option value="electronics">Electronics</option>
                    <option value="furniture">Furniture</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Serial Number</label>
                  <input type="text" name="serialNumber" value={formData.serialNumber || ''} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status || 'available'} onChange={handleInputChange}>
                    <option value="available">Available</option>
                    <option value="assigned">Assigned</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retired</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Condition</label>
                  <select name="condition" value={formData.condition || 'good'} onChange={handleInputChange}>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="old">Old</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <input type="text" name="assignedTo" value={formData.assignedTo || ''} onChange={handleInputChange} placeholder="Guard name or ID" />
                </div>
              </div>
            )}
            
            {modalType === 'uniform' && (
              <div className="form-grid">
                <div className="form-group">
                  <label>Guard Name *</label>
                  <input type="text" name="guardName" value={formData.guardName || ''} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Uniform / Equipment Type</label>
                  <select name="uniformType" value={formData.uniformType || 'Security Uniform'} onChange={handleInputChange}>
                    <option value="Security Uniform">Security Uniform</option>
                    <option value="Boots">Boots</option>
                    <option value="Belt">Belt</option>
                    <option value="Cap">Cap / Beret</option>
                    <option value="Trouser">Trouser</option>
                    <option value="Shirt">Shirt</option>
                    <option value="Jacket">Jacket</option>
                    <option value="Raincoat">Raincoat</option>
                    <option value="Umbrella">Umbrella</option>
                    <option value="Gloves">Gloves</option>
                    <option value="Helmet">Helmet / Hard Hat</option>
                    <option value="Reflective Vest">Reflective Vest</option>
                    <option value="Whistle">Whistle</option>
                    <option value="Torch">Torch / Flashlight</option>
                    <option value="Radio">Radio / Walkie-Talkie</option>
                    <option value="Baton">Baton</option>
                    <option value="Handcuffs">Handcuffs</option>
                    <option value="First Aid Kit">First Aid Kit</option>
                    <option value="Fire Extinguisher">Fire Extinguisher</option>
                    <option value="Stationery">Stationery</option>
                    <option value="ID Badge">ID Badge</option>
                    <option value="Lanyard">Lanyard</option>
                    <option value="Badge Holder">Badge Holder</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Size</label>
                  <select name="size" value={formData.size || 'M'} onChange={handleInputChange}>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Issue Date</label>
                  <input type="date" name="issueDate" value={formData.issueDate || ''} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Condition</label>
                  <select name="condition" value={formData.condition || 'good'} onChange={handleInputChange}>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="old">Old</option>
                  </select>
                </div>
              </div>
            )}
            
            {modalType === 'audit' && (
              <div className="form-grid">
                <div className="form-group form-col-2">
                  <label>Audit Title *</label>
                  <input type="text" name="title" value={formData.title || ''} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" name="date" value={formData.date || ''} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label>Auditor</label>
                  <select name="auditor" value={formData.auditor || 'Internal'} onChange={handleInputChange}>
                    <option value="Internal">Internal</option>
                    <option value="External">External</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status || 'completed'} onChange={handleInputChange}>
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div className="form-group form-col-2">
                  <label>Description</label>
                  <textarea name="description" value={formData.description || ''} onChange={handleInputChange} rows="3" />
                <div className="form-group form-col-2">
                  <label>Upload Documents (PDF, Word, Excel, Image) - Select Multiple Files</label>
                  {formData.documents && formData.documents.length > 0 && (
                    <div className="file-list" style={{marginBottom: '8px', padding: '8px', background: '#f3f4f6', borderRadius: '4px'}}>
                      <small><strong>Selected files ({formData.documents.length}):</strong></small>
                      {formData.documents.map((f, i) => (
                        <div key={i} style={{fontSize: '12px', color: '#374151'}}>📄 {f.name || f}</div>
                      ))}
                    </div>
                  )}
                  
                  <div 
                    style={{
                      marginBottom: '8px',
                      padding: '20px',
                      border: '2px dashed #ccc',
                      borderRadius: '8px',
                      textAlign: 'center',
                      background: '#f9fafb'
                    }}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#2563eb'; }}
                    onDragLeave={(e) => { e.currentTarget.style.borderColor = '#ccc'; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.style.borderColor = '#ccc';
                      const files = Array.from(e.dataTransfer.files);
                      if (files.length > 0) {
                        setFormData(prev => {
                          const existingDocs = prev.documents || [];
                          return { 
                            ...prev, 
                            documents: [...existingDocs, ...files],
                            documentNames: [...(prev.documentNames || []), ...files.map(f => f.name)]
                          };
                        });
                      }
                    }}
                  >
                    <input type="file" name="documents" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png" onChange={handleFileChange} style={{marginBottom: '8px'}} />
                    <p style={{margin: '8px 0', color: '#6b7280', fontSize: '13px'}}>
                      📁 Drag & drop files here, or click to select
                    </p>
                  </div>                                   {formData.documentUrl && (
                    <div className="form-group">
                      <AttachmentViewer attachment={{url: formData.documentUrl, name: formData.documentName || 'Document'}} />
                    </div>
                  )}
                </div>
                </div>
              </div>
            )}
            
            {modalType === 'policy' && (
              <div className="form-grid">
                <div className="form-group form-col-2">
                  <label>Policy Title *</label>
                  <input type="text" name="title" value={formData.title || ''} onChange={handleInputChange} required />
                </div>
                <div className="form-group form-col-2">
                  <label>Description</label>
                  <textarea name="description" value={formData.description || ''} onChange={handleInputChange} rows="3" />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select name="type" value={formData.type || 'policy'} onChange={handleInputChange}>
                    <option value="policy">Policy</option>
                    <option value="procedure">Procedure</option>
                    <option value="guideline">Guideline</option>
                  </select>
                <div className="form-group form-col-2">
                  <label>Upload Documents (PDF, Word, Excel, Image) - Select Multiple Files</label>
                  {formData.documents && formData.documents.length > 0 && (
                    <div className="file-list" style={{marginBottom: '8px', padding: '8px', background: '#f3f4f6', borderRadius: '4px'}}>
                      <small><strong>Selected files ({formData.documents.length}):</strong></small>
                      {formData.documents.map((f, i) => (
                        <div key={i} style={{fontSize: '12px', color: '#374151'}}>📄 {f.name || f}</div>
                      ))}
                    </div>
                  )}
                  <input type="file" name="documents" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png" onChange={handleFileChange} />
                  {formData.documentUrl && (
                    <div className="file-preview">
                      <a href={`https://www.secs.gismac.co.ke${formData.documentUrl}`} target="_blank" rel="noopener noreferrer">
                        📄 {formData.documentName || 'View Uploaded Document'}
                      </a>
                    </div>
                  )}
                </div>
                </div>
              </div>
            )}
            
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={closeModal} disabled={loading}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : (selectedItem ? 'Update' : 'Save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  const renderViewModal = () => {
    if (!viewItem) return null

    return (
      <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeView()}>
        <div className="modal-content">
          <div className="modal-header">
            <h3>Asset Details</h3>
            <button onClick={closeView}><X size={20} /></button>
          </div>
          <div className="view-details">
            <div className="detail-row">
              <span className="detail-label">Asset ID:</span>
              <span className="detail-value">{viewItem.assetId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Name:</span>
              <span className="detail-value">{viewItem.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Category:</span>
              <span className="detail-value">{viewItem.category}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Serial Number:</span>
              <span className="detail-value">{viewItem.serialNumber || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className={`detail-value status-${viewItem.status}`}>{viewItem.status}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Condition:</span>
              <span className="detail-value">{viewItem.condition}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Assigned To:</span>
              <span className="detail-value">{viewItem.assignedTo?.firstName || 'Unassigned'}</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="records">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Records Management</h1>
          <p>Assets inventory, audits, and policy documents</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal(activeTab === 'assets' ? 'asset' : activeTab === 'uniforms' ? 'uniform' : activeTab === 'audits' ? 'audit' : 'policy')}>
          <Plus size={18} />
          Add {activeTab === 'assets' ? 'Asset' : activeTab === 'uniforms' ? 'Uniform' : activeTab === 'audits' ? 'Audit' : 'Policy'}
        </button>
      </div>

      {message.text && !showModal && (
        <div className={`alert alert-${message.type} mb-3`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'assets' ? 'active' : ''}`}
          onClick={() => setActiveTab('assets')}
        >
          <Box size={18} />
          Assets Inventory
        </button>
        <button 
          className={`tab ${activeTab === 'uniforms' ? 'active' : ''}`}
          onClick={() => setActiveTab('uniforms')}
        >
          <Shield size={18} />
          Uniforms
        </button>
        <button 
          className={`tab ${activeTab === 'audits' ? 'active' : ''}`}
          onClick={() => setActiveTab('audits')}
        >
          <ClipboardList size={18} />
          Audit Reports
        </button>
        <button 
          className={`tab ${activeTab === 'tracking' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracking')}
        >
          <Package size={18} />
          Guard Tracking
        </button>
        <button 
          className={`tab ${activeTab === 'policies' ? 'active' : ''}`}
          onClick={() => setActiveTab('policies')}
        >
          <FileText size={18} />
          Policies
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'assets' && (
          <div className="assets-section">
            <div className="section-toolbar">
              <div className="search-box">
                <Search size={18} />
                <input type="text" placeholder="Search assets..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <div className="filter-buttons">
                <button className="btn btn-outline" onClick={handleExport}>
                  <Download size={18} />
                  Export
                </button>
                <button className="btn btn-primary" onClick={() => openModal('asset')}>
                  <Plus size={18} />
                  Add Asset
                </button>
              </div>
            </div>
            <div className="assets-grid">
              {filteredAssets.length > 0 ? filteredAssets.map(asset => (
                <div key={asset._id} className="asset-card">
                  <div className="asset-header">
                    <span className="asset-id">{asset.assetId}</span>
                    <span className={`asset-status ${asset.status}`}>{asset.status}</span>
                  </div>
                  <h4>{asset.name}</h4>
                  <p className="asset-category">{asset.category}</p>
                  <div className="asset-details">
                    <p><strong>Serial:</strong> {asset.serialNumber || 'N/A'}</p>
                    <p><strong>Assigned:</strong> {asset.assignedTo?.firstName || 'Unassigned'}</p>
                    <p><strong>Condition:</strong> {asset.condition}</p>
                  </div>
                  <div className="asset-actions">
                    <button className="btn-icon" onClick={() => openView(asset)} title="View"><Eye size={16} /></button>
                    {isAdmin && (
  <>
    <button className="btn-icon" onClick={() => openModal('asset', asset)} title="Edit"><Edit size={16} /></button>
    <button className="btn-icon btn-danger" onClick={() => handleDelete(asset, 'asset')} title="Delete"><Trash2 size={16} /></button>
  </>
)}
                  </div>
                </div>
              )) : (
                <div className="text-center py-4">No assets found</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'uniforms' && (
          <div className="uniforms-section">
            <div className="section-toolbar">
              <button className="btn btn-primary" onClick={() => openModal('uniform')}>
                <Plus size={18} />
                Add Uniform
              </button>
            </div>
            <div className="uniforms-stats">
              <div className="uniform-stat">
                <h3>Total Staff</h3>
                <span className="stat-value">{uniforms.length}</span>
              </div>
              <div className="uniform-stat">
                <h3>Issued</h3>
                <span className="stat-value">{uniforms.filter(d => d.uniformStatus === 'issued').length}</span>
              </div>
              <div className="uniform-stat">
                <h3>Pending</h3>
                <span className="stat-value">{uniforms.filter(d => d.uniformStatus === 'pending').length}</span>
              </div>
              <div className="uniform-stat">
                <h3>Active Staff</h3>
                <span className="stat-value">{uniforms.filter(d => d.staffStatus === 'active').length}</span>
              </div>
            </div>
            <div className="uniforms-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>Guard</th>
                    <th>Uniform Type</th>
                    <th>Size</th>
                    <th>Issue Date</th>
                    <th>Condition</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {uniforms.length === 0 ? (
                    <tr><td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>No uniform records found</td></tr>
                  ) : (
                    uniforms.map(item => (
                      <tr key={item._id}>
                        <td>
                          <div className="cell-primary">{item.guardName || 'N/A'}</div>
                          <div className="cell-secondary">{item.position || ''}</div>
                        </td>
                        <td>{item.uniformType || 'Security Uniform'}</td>
                        <td>{item.size || 'M'}</td>
                        <td>{item.issueDate ? new Date(item.issueDate).toLocaleDateString() : 'Not issued'}</td>
                        <td>
                          <span className={'badge badge-' + (item.condition === 'new' || item.condition === 'good' ? 'success' : item.condition === 'fair' ? 'warning' : 'danger')}>
                            {item.condition || 'Good'}
                          </span>
                        </td>
                        <td>
                          {isAdmin && (
  <>
    <button className="btn btn-sm btn-outline" onClick={() => openModal('uniform', item)}>Edit</button>
    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item, 'uniform')}>Delete</button>
  </>
)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'audits' && (
         <div className="audits-section">
  <div className="section-toolbar" style={{display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center'}}>
    <button className="btn btn-primary" onClick={() => openModal('audit')}>
      <Plus size={18} />
      Add Audit
    </button>
    <input 
      type="text" 
      placeholder="Filter audits..." 
      value={auditFilter} 
      onChange={e => setAuditFilter(e.target.value)}
      style={{padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd'}}
    />
    <input 
      type="date" 
      value={auditDateFrom} 
      onChange={e => setAuditDateFrom(e.target.value)}
      style={{padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd'}}
    />
    <input 
      type="date" 
      value={auditDateTo} 
      onChange={e => setAuditDateTo(e.target.value)}
      style={{padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd'}}
    />
    <button className="btn btn-secondary" onClick={handleExportAudits}>
      Export Excel
    </button>
  </div> 
          <div className="audit-list">
              {audits.length === 0 && <div className="text-center py-4">No audits found.</div>}
              {audits.filter(audit => {
  if (!auditFilter && !auditDateFrom && !auditDateTo) return true
  const matchesFilter = !auditFilter || (
    (audit.title || '').toLowerCase().includes(auditFilter.toLowerCase()) ||
    (audit.description || '').toLowerCase().includes(auditFilter.toLowerCase()) ||
    (audit.auditor || '').toLowerCase().includes(auditFilter.toLowerCase())
  )
  const matchesFrom = !auditDateFrom || new Date(audit.date) >= new Date(auditDateFrom)
  const matchesTo = !auditDateTo || new Date(audit.date) <= new Date(auditDateTo)
  return matchesFilter && matchesFrom && matchesTo
}).map((audit, idx) => (
                <div className="audit-card" key={idx}>
                  <div className="audit-header">
                    <h4>{audit.title || "Untitled"}</h4>
                    <span className="audit-date">{audit.date ? new Date(audit.date).toLocaleDateString("en-KE") : "N/A"}</span>
                  </div>
                  <p>{audit.description || "No description"}</p>
                  <div className="audit-actions">
                            <DocumentList documents={audit.documents} singleUrl={audit.documentUrl} singleName={audit.documentName} label="Audit Report" />
                    <button className="btn btn-outline" onClick={() => openModal("audit", audit)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(audit, "audit")}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'tracking' && (
          <GuardTracking />
        )}

        {activeTab === 'policies' && (
          <div className="policies-section">
            <div className="section-toolbar">
              <button className="btn btn-primary" onClick={() => openModal('policy')}>
                <Plus size={18} />
                Add Policy
              </button>
            </div>
            <div className="policies-grid">
              {policies.length === 0 && <div className="text-center py-4">No policies found.</div>}
              {policies.map((policy, idx) => (
                <div className="policy-card" key={idx}>
                  <FileText size={32} />
                  <h4>{policy.title || "Untitled"}</h4>
                  <p>{policy.description || "No description"}</p>
                  <div className="policy-actions">
                          <DocumentList documents={policy.documents} singleUrl={policy.documentUrl} singleName={policy.documentName} label="Policy Document" />
                    <button className="btn btn-outline" onClick={() => openModal("policy", policy)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(policy, "policy")}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {renderModal()}
      {renderViewModal()}
    </div>
  )
}
